/**
 * Hook for fetching Shop By Price statistics (added gifts count and recommended count per price range)
 *
 * Wired to RIG API via RegistryItemGatewayApiService
 * Reference FEMR implementation:
 * - contexts/GiftAdvisorSummaryContext.js::fetchGiftAdvisorSummary
 * - services/RegistryItemGatewayApi.js::getGiftAdvisorSummary
 *
 * Expected API response shape (from FEMR):
 * {
 *   giftBuckets: [
 *     {
 *       added: number,        // Number of gifts added in this range
 *       total: number,        // Recommended total for this range
 *       shopLinkParam: string, // e.g., '%2A_49.99', '50.00_99.99'
 *       simpleLabel: string,  // e.g., '0-49', '50-99'
 *       label: string         // e.g., '$0-$49 gifts'
 *     }
 *   ]
 * }
 */

import { useState, useEffect } from 'react';
import RegistryItemGatewayApiService, {
  GiftBucket,
} from '../services/registry-item-gateway-api/registry-item-gateway-api.service';
import { useWeddingWebsiteQuery } from '../generated/graphql';

export interface ShopByPriceStats {
  addedCount: number;
  recommendedMoreCount: number;
}

export interface GiftAdvisorSummary {
  giftBuckets: GiftBucket[];
}

/**
 * Finds the bucket matching the given shopLinkParam
 * Ported from FEMR: src/views/Discover/components/ShopByPrice.js::getCurrentBucket
 */
const getCurrentBucket = (giftSummary: GiftAdvisorSummary, shopLinkParam: string): GiftBucket | undefined => {
  return giftSummary.giftBuckets.find(e => e.shopLinkParam === shopLinkParam);
};

/**
 * Calculates how many more gifts to recommend
 * Ported from FEMR: src/views/Discover/components/ShopByPrice.js::getRecommendedCount
 */
const getRecommendedCount = ({ total, added }: { total: number; added: number }): number => {
  const count = total - added;
  return count < 0 ? 0 : count;
};

/**
 * Hook for fetching Shop By Price statistics
 *
 * Wired to RIG API via RegistryItemGatewayApiService
 *
 * @param shopLinkParam - Price range key (e.g., '%2A_49.99', '50.00_99.99')
 * @param numberOfGuests - Guest count range (e.g., "101–150") (optional)
 * @param refetchTrigger - Optional dependency to trigger refetch (e.g., a counter that increments)
 * @returns Statistics for the selected price range
 */
export const useShopByPriceStats = (
  shopLinkParam: string,
  numberOfGuests?: string,
  refetchTrigger?: number
): ShopByPriceStats & { isLoading: boolean; refetch: () => void } => {
  const [giftAdvisorSummary, setGiftAdvisorSummary] = useState<GiftAdvisorSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refetchCounter, setRefetchCounter] = useState(0);

  const { loading: isWeddingWebsiteLoading } = useWeddingWebsiteQuery();

  const fetchGiftAdvisorSummary = async () => {
    if (isWeddingWebsiteLoading) return;

    if (!numberOfGuests) {
      setIsLoading(false);
      setGiftAdvisorSummary({ giftBuckets: [] });

      return;
    }

    try {
      setIsLoading(true);

      const response = await RegistryItemGatewayApiService.getGiftAdvisorSummary({
        numberOfGuests,
      });

      setGiftAdvisorSummary(response);
      setError(null);
    } catch (err) {
      console.error('[useShopByPriceStats] Error fetching gift advisor summary:', err);
      setError(err as Error);
      setGiftAdvisorSummary({ giftBuckets: [] });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchGiftAdvisorSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchCounter, refetchTrigger, shopLinkParam, numberOfGuests]);

  const refetch = () => {
    setRefetchCounter(prev => prev + 1);
  };

  const currentBucket = giftAdvisorSummary ? getCurrentBucket(giftAdvisorSummary, shopLinkParam) : undefined;

  if (!currentBucket || error) {
    return {
      addedCount: 0,
      recommendedMoreCount: 0,
      isLoading,
      refetch,
    };
  }

  return {
    addedCount: currentBucket.added,
    recommendedMoreCount: getRecommendedCount(currentBucket),
    isLoading,
    refetch,
  };
};

/**
 * Formats bucket label for display in blurb text
 * Ported from FEMR: src/views/Discover/components/ShopByPrice.js::formatBucketLabel
 */
export const formatBucketLabel = (bucket: GiftBucket): string => {
  return bucket.simpleLabel === '150+' ? '$150+' : bucket.label.replace(' gifts', '');
};
